import { useState } from "react";
import SelectSearch, { SelectOption } from "../../common/forms/SelectSearch";
import Container, { Progress } from "../../common/Container";
import Footer from "../../common/Footer";
import FooterButton from "../../common/FooterButton";
import { Destination, LoadoutGroup, Order, SODestination } from "../../UserFlow";
import { FooterProps } from "./StageOrderTypes";
import SwitchToMobileQRCode from "../../common/SwitchToMobileQRCode";
import useIsAnonymous from "../../common/lib/hooks/useIsAnonymous";
import { useLingui } from "@lingui/react";
import { msg, Plural, Trans } from "@lingui/macro";
import StartOverButton from "../../common/StartOverButton";
import ExpandableText from "../../common/ExpandableText";

export interface SelectDestinationProps extends FooterProps {
  allowDestinationChange: boolean;
  allowEnteredShipTo: boolean;
  requireDestination: boolean;
  destinations: Array<Destination>;
  order?: Order,
  loadoutGroup?: LoadoutGroup,
  initialDestination?: SODestination;
  destinationSelected: (d?: SODestination) => void;
  progress?: Progress;
  startOver: () => void;
}

const SelectDestination = (props: SelectDestinationProps) => {
  const { _ } = useLingui();
  const isAnonymous = useIsAnonymous();
  const [destination, setDestination] = useState(props.initialDestination);
  const OtherOption: SelectOption = { value: 'f02e4784-8ab1-4b44-bs7c4-9b91bb2e5637', name: _(msg`Other...`) };

  const initialDropdownValue = (initialDestination?: SODestination) => {
    if (!initialDestination) return "";

    if ("id" in initialDestination) {
      return initialDestination.id;
    }

    return OtherOption.value;
  }

  const [selectValue, setSelectValue] = useState(initialDropdownValue(props.initialDestination));

  const destinations = props.destinations.concat(props.order?.customer.destinations ?? []);

  const destOptions = destinations.map<SelectOption>(d => {
    const otherDestinationWithSameName = destinations.some(dto => dto.name === d.name && dto.id !== d.id);

    var destOptionName = `${d.name}`;

    if (otherDestinationWithSameName) {
      if (!!d.city && !!d.state) {
        destOptionName += ` (${d.city}, ${d.state})`;
      } else if (!!d.city) {
        destOptionName += ` (${d.city})`;
      } else if (!!d.state) {
        destOptionName += ` (${d.state})`;
      }
    }
    
    return { value: d.id, name: destOptionName };
  });
  if (props.allowEnteredShipTo) { destOptions.push(OtherOption); }

  const destinationSelected = (value: string) => {
    if (value === OtherOption.value) {
      setDestination({ name: "" });
    } else {
      setDestination(destinations.find(d => d.id === value));
    }

    setSelectValue(value);
  }

  const dropdownAvailable = props.allowDestinationChange && destOptions.length > 0;

  const formWidth = isAnonymous ? "w-65" : "w-75";

  return <Container
    title={_(msg`Load Information`)}
    subtitle={_(msg`Where are you taking the load?`)}
    progress={props.progress} >

    {isAnonymous && <>
      <StartOverButton startOver={props.startOver}/>
      <SwitchToMobileQRCode />
    </>}
    <div className={formWidth + " m-auto"}>
      <label className="h2" htmlFor="orderNumber"><Trans>Enter Order Number</Trans></label>
      <input
        type="text"
        id="orderNumber"
        className="form-control form-control-lg"
        style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}
        value={props.order?.number ?? props.loadoutGroup!.name + " (" + props.loadoutGroup?.orderNumbers.join(", ") + ")"}
        disabled />
      <label className="h5 mt-4 mb-0">
        <Plural 
          value={props.order?.products.length ?? props.loadoutGroup!.products.length} 
          one="Product"
          other="Products"/>
      </label>
      <ExpandableText
        text={props.order?.products.join(", ") ?? props.loadoutGroup!.products.join(", ")}
        lines={2}
        expandTestId="products-expand"
      />
      <label className="h5 mt-4 mb-0">
        <Plural 
          value={props.loadoutGroup?.customers.length ?? 1} 
          one="Customer"
          other="Customers"/>
      </label>
      <ExpandableText
        text={props.order?.customer.name ?? props.loadoutGroup!.customers.join(", ")}
        lines={2}
        expandTestId="customers-expand"
      />
      <label className="h5 mt-4" htmlFor="destination">
        <Plural 
          value={props.loadoutGroup?.destinations.length ?? 1} 
          one="Destination"
          other="Destinations"/>
      </label>
      {props.order && <><SelectSearch
        id="destination"
        dataTestId="destination-selector"
        disabled={!dropdownAvailable}
        options={destOptions}
        hideSearch={destOptions.length < 5}
        selectedValue={selectValue}
        placeholder={dropdownAvailable ? _(msg`Select a destination...`) : _(msg`No destinations available`)}
        onChange={destinationSelected} />
        {
          selectValue === OtherOption.value &&
          <input
            className="form-control mt-4"
            type="text"
            placeholder={_(msg`Enter destination name...`)}
            onChange={ev => setDestination({ name: ev.target.value })}
            value={destination?.name} 
            autoComplete={!isAnonymous ? "on" : "off"}/>
        }
      </>}
      {props.loadoutGroup && <ExpandableText
        text={props.loadoutGroup!.destinations.join(", ")}
        lines={2}
        expandTestId="destinations-expand"
      />}
    </div>
    <Footer
      backButton={<FooterButton {...props.backButton} />}
      nextButton={
        <FooterButton
          disabled={(selectValue === OtherOption.value && !destination?.name) || (props.requireDestination && !destination && !props.loadoutGroup)}
          onClick={() => props.destinationSelected(destination)}>
          <Trans>Next</Trans>
        </FooterButton>
      }
    />
  </Container>
}

export default SelectDestination;
